import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../App';
import { formatBytes, timeAgo, formatDateTime, DEFAULT_TZ, useIsMobile } from '../utils';
import { ServersOverviewCard } from '../components/dashboard/ServersOverviewCard';
import { sumMonthlyTotals } from '../components/MonthlyPriceCard';

const statusColor = { ok: 'var(--green)', running: 'var(--accent)', error: 'var(--red)', warning: 'var(--yellow)' };

function ActivityChart({ activity }) {
  const maxBar = Math.max(...activity.map(a => (a.ok || 0) + (a.error || 0) + (a.warning || 0)), 1);
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2,
                       textTransform: 'uppercase', color: 'var(--muted)' }}>
          Actividad últimos 14 días
        </span>
        <span style={{ fontSize: 11, fontFamily: 'var(--mono)' }}>
          <span style={{ color: 'var(--accent)' }}>■</span>
          <span style={{ color: 'var(--muted)', marginLeft: 4 }}>ok</span>
          <span style={{ marginLeft: 12, color: 'var(--yellow)' }}>■</span>
          <span style={{ color: 'var(--muted)', marginLeft: 4 }}>warn</span>
          <span style={{ marginLeft: 12, color: 'var(--red)' }}>■</span>
          <span style={{ color: 'var(--muted)', marginLeft: 4 }}>error</span>
        </span>
      </div>
      <div style={{ padding: 20, display: 'flex', gap: 4, alignItems: 'flex-end', height: 100 }}>
        {activity.map((a, i) => {
          const isToday = i === activity.length - 1;
          const ok      = a.ok      || 0;
          const warn    = a.warning || 0;
          const err     = a.error   || 0;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column',
                                  alignItems: 'center', gap: 4, height: '100%' }}>
              <div style={{ flex: 1, width: '100%', display: 'flex',
                            flexDirection: 'column', justifyContent: 'flex-end' }}>
                {err  > 0 && <div style={{ height: `${(err  / maxBar) * 100}%`,
                                           background: 'var(--red)',    opacity: 0.8 }} />}
                {warn > 0 && <div style={{ height: `${(warn / maxBar) * 100}%`,
                                           background: 'var(--yellow)', opacity: 0.8 }} />}
                {ok   > 0 && <div style={{ height: `${(ok   / maxBar) * 100}%`,
                                           background: 'var(--accent)',
                                           opacity: isToday ? 1 : 0.6,
                                           borderRadius: '2px 2px 0 0',
                                           boxShadow: isToday ? '0 0 8px var(--accent)' : 'none' }} />}
              </div>
              <div style={{ fontSize: 9, fontFamily: 'var(--mono)',
                            color: isToday ? 'var(--accent)' : 'var(--muted)' }}>
                {isToday ? 'hoy' : (a.date || '').slice(8)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClientDashboard({ clientId }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [client, setClient]     = useState(null);
  const [servers, setServers]   = useState([]);
  const [runs, setRuns]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([api.getClient(clientId), api.getServers(clientId), api.getRecentRuns(20), api.summary(), api.activity()])
      .then(([c, s, r, sum, act]) => { setClient(c); setServers(s); setRuns(r); setSummary(sum); setActivity(act); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;
  if (!client) return null;

  const okRuns    = runs.filter(r => r.status === 'ok').length;
  const warnRuns  = runs.filter(r => r.status === 'warning').length;
  const errRuns   = runs.filter(r => r.status === 'error').length;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>{client.name}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>Plan {client.plan?.name} · {client.city || '—'}</div>
      </div>

      {/* STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Servidores',     value: servers.length,             color: 'var(--accent)' },
          { label: 'Jobs exitosos',  value: okRuns,                     color: 'var(--green)' },
          { label: 'Warnings',       value: warnRuns,                   color: warnRuns > 0 ? 'var(--yellow)' : 'var(--muted)' },
          { label: 'Errores',        value: errRuns,                    color: errRuns > 0 ? 'var(--red)' : 'var(--muted)' },
          { label: 'Storage usado',  value: formatBytes((client.storage_used_gb ?? 0) * 1e9), color: (client.storage_pct ?? 0) > 80 ? 'var(--yellow)' : 'var(--accent)' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.color }} />
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 10 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* STORAGE BAR */}
      {!client.internal && <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Storage</div>
        {client.storage_quota_gb != null ? (() => {
          const p     = client.storage_pct ?? 0;
          const color = p >= 90 ? 'var(--red)' : p >= 85 ? 'var(--yellow)' : 'var(--accent)';
          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6, fontFamily: 'var(--mono)' }}>
                <span style={{ color: 'var(--text)' }}>
                  {(client.storage_used_gb ?? 0).toFixed(1)} GB <span style={{ color: 'var(--muted)' }}>/ {client.storage_quota_gb} GB</span>
                </span>
                <span style={{ color }}>{p.toFixed(1)}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(p, 100)}%`, background: color, borderRadius: 3, transition: 'width 0.5s' }} />
              </div>
              {p >= 85 && (
                <div style={{ marginTop: 6, fontSize: 11, color, fontFamily: 'var(--mono)' }}>
                  {p >= 90 ? '⚠ STORAGE CRÍTICO' : '⚠ Storage alto'}
                </div>
              )}
            </div>
          );
        })() : (
          <span style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Sin plan asignado</span>
        )}
      </div>}

      {/* SERVIDORES */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Servidores</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {servers.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'var(--mono)' }}>Sin servidores registrados</div>}
          {servers.map((s, i) => (
            <div key={i} style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '12px 16px', minWidth: 180 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{s.hostname}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontFamily: 'var(--mono)' }}>{s.ip_address || '—'}</div>
              <div style={{ fontSize: 11, color: s.last_seen ? 'var(--green)' : 'var(--muted)', marginTop: 4 }}>
                {s.last_seen ? `Visto ${timeAgo(s.last_seen)}` : 'Sin contacto'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ÚLTIMOS RUNS */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Últimos Backups</span>
        </div>
        <div style={{ padding: 20 }}>
          {runs.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'var(--mono)' }}>Sin backups todavía</div>}
          {runs.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < runs.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor[r.status] || 'var(--muted)', boxShadow: `0 0 6px ${statusColor[r.status] || 'var(--muted)'}`, flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 500 }}>{r.job_name}</span>
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>{r.server}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{formatBytes(r.size_bytes)}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}
                    title={timeAgo(r.started_at)}>
                {formatDateTime(r.started_at, r.server_timezone || DEFAULT_TZ)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {activity.length > 0 && <ActivityChart activity={activity} />}
    </div>
  );
}

function AdminDashboard() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { isAdmin, isOperator, perms } = useAuth();
  // #201: usuarios online, almacenamiento y facturación son del admin; el operador sin can_view_clients no ve nada de
  // los clientes (el backend le devolvería listas vacías o 403): ni se piden.
  const blindOperator = isOperator && !perms?.can_view_clients;
  const [summary, setSummary]             = useState(null);
  const [runs, setRuns]                   = useState([]);
  const [activity, setActivity]           = useState([]);
  const [storageUpdatedAt, setStorageUpdatedAt] = useState(null);
  const [storageSummary, setStorageSummary]   = useState(null);
  const [onlineUsers, setOnlineUsers]         = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [mrr, setMrr]                         = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [toast, setToast]                     = useState({ msg: '', type: '' });
  const showToast = (msg, type = 'error') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 3000); };

  useEffect(() => {
    if (blindOperator) { setLoading(false); return; }
    Promise.all([
      api.summary(), api.getRecentRuns(), api.activity(), api.storageUpdated(),
      isAdmin ? api.onlineUsers() : Promise.resolve([]),
      isAdmin ? api.storageSummary() : Promise.resolve(null),
    ])
      .then(([s, r, a, su, ou, ss]) => { setSummary(s); setRuns(r); setActivity(a); setStorageUpdatedAt(su?.updated_at || null); setOnlineUsers(ou || []); setStorageSummary(ss); })
      .catch(console.error)
      .finally(() => setLoading(false));
    api.getClients().then(clients => {
      const total = clients.reduce((sum, c) => sum + (c.monthly_price?.total || 0), 0);
      setMrr(total);
    }).catch(() => {});
  }, [isAdmin, blindOperator]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(() => {
      api.onlineUsers().then(ou => setOnlineUsers(ou || [])).catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [isAdmin]);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;

  if (blindOperator) return (
    <div style={{ padding: 20, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 13 }}>
      Tu cuenta no tiene permiso para ver clientes. Pedile al administrador el permiso «Ver clientes».
    </div>
  );

  const stats = [
    { label: 'Clientes Activos',  value: summary?.total_clients ?? 0,  sub: 'registrados',        color: 'var(--accent)' },
    { label: 'Jobs Exitosos hoy', value: summary?.ok_today ?? 0,       sub: `de ${(summary?.ok_today ?? 0) + (summary?.warnings_today ?? 0) + (summary?.errors_today ?? 0)} totales hoy`, color: 'var(--green)' },
    { label: 'Warnings hoy',      value: summary?.warnings_today ?? 0, sub: 'completados con advertencias', color: summary?.warnings_today > 0 ? 'var(--yellow)' : 'var(--muted)' },
    { label: 'Errores hoy',       value: summary?.errors_today ?? 0,   sub: 'requieren atención', color: summary?.errors_today > 0 ? 'var(--red)' : 'var(--muted)' },
    { label: 'Storage Total',     value: formatBytes((summary?.storage_total_gb ?? 0) * 1e9), sub: 'almacenamiento total', color: 'var(--yellow)', extra: storageUpdatedAt },
    { label: 'MRR Estimado',      value: mrr != null ? `$${mrr.toFixed(0)}` : '—', sub: 'ingresos mensuales estimados', color: 'var(--green)' },
  ];

  return (
    <div>
      {toast.msg && (
        <div style={{ marginBottom: 12, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12, background: toast.type === 'ok' ? 'rgba(0,229,160,0.1)' : 'rgba(255,77,109,0.1)', border: `1px solid ${toast.type === 'ok' ? 'var(--green)' : 'var(--red)'}`, color: toast.type === 'ok' ? 'var(--green)' : 'var(--red)' }}>
          {toast.type === 'ok' ? '✓' : '✕'} {toast.msg}
        </div>
      )}
      {isAdmin && (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          onClick={async () => {
            setDownloadingReport(true);
            try {
              const blob = await api.billingReport();
              const url  = URL.createObjectURL(blob);
              const a    = document.createElement('a');
              const now  = new Date();
              a.href     = url;
              a.download = `openbackup_facturacion_${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}.xlsx`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) {
              showToast(e.message);
            } finally {
              setDownloadingReport(false);
            }
          }}
          disabled={downloadingReport}
          style={{
            background: 'transparent',
            border: '1px solid var(--green)',
            color: 'var(--green)',
            padding: '7px 16px',
            fontSize: 11,
            fontFamily: 'var(--mono)',
            cursor: downloadingReport ? 'not-allowed' : 'pointer',
            opacity: downloadingReport ? 0.5 : 1,
          }}
        >
          {downloadingReport ? 'GENERANDO...' : '⬇ REPORTE FACTURACIÓN'}
        </button>
      </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {stats.map((s, i) => (
          <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.color }} />
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 10 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color: s.color, marginBottom: 6 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{s.sub}</div>
            {s.extra !== undefined && (
              <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                {s.extra ? `act. ${timeAgo(s.extra)}` : 'sin datos'}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (isAdmin ? '2fr 1fr 1fr' : '2fr 1fr'), gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Últimos Jobs</span>
            <button onClick={() => navigate('/jobs')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Ver todos</button>
          </div>
          <div style={{ maxHeight: 340, overflowY: 'auto', padding: 20 }}>
            {runs.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'var(--mono)' }}>Sin runs todavía</div>}
            {runs.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: i < runs.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor[r.status] || 'var(--muted)', boxShadow: `0 0 6px ${statusColor[r.status] || 'var(--muted)'}`, flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 500 }}>{r.job_name}</span>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>{r.client}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{formatBytes(r.size_bytes)}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}
                      title={timeAgo(r.started_at)}>
                  {formatDateTime(r.started_at, r.server_timezone || DEFAULT_TZ)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Estado del Sistema</span>
          </div>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Clientes activos',       val: summary?.total_clients ?? 0,  color: 'var(--accent)' },
              { label: 'Servidores monitoreados', val: summary?.total_servers ?? 0,  color: 'var(--green)' },
              { label: 'Jobs completados hoy',    val: summary?.ok_today ?? 0,       color: 'var(--green)' },
              { label: 'Errores hoy',             val: summary?.errors_today ?? 0,   color: summary?.errors_today > 0 ? 'var(--red)' : 'var(--muted)' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                <span style={{ color: 'var(--muted)' }}>{item.label}</span>
                <span style={{ fontFamily: 'var(--mono)', color: item.color, fontWeight: 700 }}>{item.val}</span>
              </div>
            ))}
          </div>
        </div>

        {isAdmin && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Usuarios Online</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 700, color: onlineUsers.length > 0 ? 'var(--green)' : 'var(--muted)' }}>{onlineUsers.length}</span>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {onlineUsers.length === 0 && (
              <div style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'var(--mono)' }}>Ninguno ahora</div>
            )}
            {onlineUsers.map((u, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 5px var(--green)', flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>{u.role}</span>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* SERVERS OVERVIEW */}
      <div style={{ marginBottom: 16 }}>
        <ServersOverviewCard userRole="admin" />
      </div>

      {/* STORAGE PANEL */}
      {isAdmin && (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 16 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Almacenamiento</span>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          {!storageSummary ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Cargando métricas de storage…</div>
          ) : (
            <>
              {/* Global metric cards */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 16, marginBottom: 20 }}>
                {[
                  { label: 'Uso interno OpenBackup', value: `${storageSummary.internal_used_gb.toFixed(1)} GB`, sub: storageSummary.total_consumed_gb ? `${(storageSummary.internal_used_gb / storageSummary.total_consumed_gb * 100).toFixed(1)}% del consumo` : null, color: 'var(--yellow)' },
                  { label: 'Uso clientes', value: `${storageSummary.per_client.reduce((s, c) => s + (c.used_gb || 0), 0).toFixed(1)} GB`, sub: `${storageSummary.per_client.length} cliente${storageSummary.per_client.length !== 1 ? 's' : ''}`, color: 'var(--accent)' },
                  { label: 'Consumido total', value: `${storageSummary.total_consumed_gb.toFixed(1)} GB`, sub: 'interno + clientes', color: 'var(--green)' },
                ].map((c, i) => (
                  <div key={i} style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: 16 }}>
                    <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 8 }}>{c.label}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 22, fontWeight: 700, color: c.color, marginBottom: 4 }}>{c.value}</div>
                    {c.sub && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.sub}</div>}
                  </div>
                ))}
              </div>
              {/* Per-client table */}
              {isMobile ? (
                <div>
                  {storageSummary.per_client.map(c => {
                    const pct   = c.pct ?? 0;
                    const color = pct >= 90 ? 'var(--red)' : pct >= 85 ? 'var(--yellow)' : 'var(--accent)';
                    return (
                      <div key={c.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>CLIENTE</div>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                          {pct >= 85 && <span style={{ color: 'var(--yellow)', marginRight: 4 }}>⚠</span>}{c.name}
                          {c.covert_copy_enabled && (
                            <span style={{ marginLeft: 8, fontFamily: 'var(--mono)', fontSize: 10, padding: '1px 6px', borderRadius: 3, border: `1px solid ${c.covert_copy_used_gb == null ? 'var(--yellow)' : 'var(--green)'}`, color: c.covert_copy_used_gb == null ? 'var(--yellow)' : 'var(--green)' }}>
                              🛡 {c.covert_copy_used_gb == null ? 's/datos' : `${c.covert_copy_used_gb.toFixed(1)} GB`}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>ASIGNADO</div>
                            <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>{c.quota_gb.toFixed(1)} GB</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>USADO</div>
                            <div style={{ fontSize: 12, fontFamily: 'var(--mono)' }}>{c.used_gb.toFixed(1)} GB</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color, minWidth: 36, textAlign: 'right' }}>
                            {c.pct != null ? `${c.pct}%` : '—'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Cliente', 'Reseller', 'Asignado', 'Usado', 'Consumido'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {storageSummary.per_client.map((c, i) => {
                      const pct   = c.pct ?? 0;
                      const color = pct >= 90 ? 'var(--red)' : pct >= 85 ? 'var(--yellow)' : 'var(--accent)';
                      return (
                        <tr key={c.id} style={{ borderBottom: i < storageSummary.per_client.length - 1 ? '1px solid var(--border)' : 'none' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                            {pct >= 85 && <span style={{ color: 'var(--yellow)', marginRight: 6 }}>⚠</span>}
                            {c.name}
                            {c.covert_copy_enabled && (
                              <span style={{ marginLeft: 8, fontFamily: 'var(--mono)', fontSize: 10, padding: '1px 6px', borderRadius: 3, border: `1px solid ${c.covert_copy_used_gb == null ? 'var(--yellow)' : 'var(--green)'}`, color: c.covert_copy_used_gb == null ? 'var(--yellow)' : 'var(--green)' }}>
                                🛡 {c.covert_copy_used_gb == null ? 's/datos' : `${c.covert_copy_used_gb.toFixed(1)} GB`}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--muted)' }}>{c.reseller_name || '—'}</td>
                          <td style={{ padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>{c.quota_gb.toFixed(1)} GB</td>
                          <td style={{ padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 12 }}>{c.used_gb.toFixed(1)} GB</td>
                          <td style={{ padding: '10px 12px', minWidth: 180 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 3 }} />
                              </div>
                              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color, minWidth: 36, textAlign: 'right' }}>
                                {c.pct != null ? `${c.pct}%` : '—'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}

          {storageSummary?.wasabi_updated_at && (
            <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Consumo real Wasabi</span>
                <span style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                  datos al {new Date(storageSummary.wasabi_updated_at).toLocaleString()} · recalculado por Wasabi 1×/día
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 16, marginBottom: 16 }}>
                {[
                  { label: 'Activo',            value: `${(storageSummary.wasabi_active_gb ?? 0).toFixed(1)} GB`,  sub: 'objetos vivos',                                           color: 'var(--accent)' },
                  { label: 'Borrado facturable', value: `${(storageSummary.wasabi_deleted_gb ?? 0).toFixed(1)} GB`, sub: 'cobrado hasta 90 días',                                  color: 'var(--red)' },
                  { label: 'Costo estimado',     value: `$${(storageSummary.wasabi_cost_usd ?? 0).toFixed(2)}`,    sub: `USD/mes · $${storageSummary.wasabi_rate_usd_per_tb}/TB`, color: 'var(--green)' },
                ].map((c, i) => (
                  <div key={i} style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: 16 }}>
                    <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 8 }}>{c.label}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 22, fontWeight: 700, color: c.color, marginBottom: 4 }}>{c.value}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.sub}</div>
                  </div>
                ))}
              </div>

              {(() => {
                const rows = [
                  ...(storageSummary.per_client || [])
                    .filter(c => (c.wasabi_deleted_gb ?? 0) > 0)
                    .map(c => ({ name: c.name, deleted: c.wasabi_deleted_gb, cost: c.wasabi_cost_usd })),
                  ...(storageSummary.wasabi_internal && (storageSummary.wasabi_internal.deleted_gb ?? 0) > 0
                    ? [{ name: 'Interno (OpenBackup)', deleted: storageSummary.wasabi_internal.deleted_gb, cost: storageSummary.wasabi_internal.cost_usd }]
                    : []),
                  ...((storageSummary.wasabi_unassigned?.deleted_gb ?? 0) > 0
                    ? [{ name: 'Sin asignar / otros', deleted: storageSummary.wasabi_unassigned.deleted_gb, cost: storageSummary.wasabi_unassigned.cost_usd }]
                    : []),
                ].sort((a, b) => b.deleted - a.deleted);
                if (rows.length === 0) return null;
                return (
                  <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: 16 }}>
                    <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 10 }}>
                      Borrado facturable por origen
                    </div>
                    {rows.map((r, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontFamily: 'var(--mono)', padding: '6px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                        <span style={{ color: 'var(--red)', width: 110, textAlign: 'right' }}>{r.deleted.toFixed(1)} GB</span>
                        <span style={{ color: 'var(--muted)', width: 90, textAlign: 'right' }}>${(r.cost ?? 0).toFixed(2)}</span>
                      </div>
                    ))}
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 10 }}>
                      El borrado se absorbe (no se factura al cliente); se muestra como costo propio.
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
      )}

      <ActivityChart activity={activity} />
    </div>
  );
}

function ResellerDashboard() {
  const isMobile  = useIsMobile();
  const navigate  = useNavigate();
  const { resellerPerms, me } = useAuth();
  const [clients, setClients]   = useState([]);
  const [runs, setRuns]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([api.getClients(), api.getRecentRuns(20), api.summary(), api.activity()])
      .then(([c, r, s, a]) => { setClients(c); setRuns(r); setSummary(s); setActivity(a); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;

  const okRuns   = runs.filter(r => r.status === 'ok').length;
  const warnRuns = runs.filter(r => r.status === 'warning').length;
  const errRuns  = runs.filter(r => r.status === 'error').length;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Dashboard</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{clients.length} clientes · resumen de actividad</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Mis Clientes',    value: clients.length, color: 'var(--yellow)', show: true },
          { label: 'Costo mensual',   value: `$${sumMonthlyTotals(clients).toFixed(2)}`, color: 'var(--green)', show: !!resellerPerms?.can_view_billing },
          { label: 'Jobs exitosos',   value: okRuns,          color: 'var(--green)',  show: !!(resellerPerms?.can_manage_jobs || resellerPerms?.can_view_reports) },
          { label: 'Warnings',        value: warnRuns,        color: warnRuns > 0 ? 'var(--yellow)' : 'var(--muted)', show: !!(resellerPerms?.can_manage_jobs || resellerPerms?.can_view_reports) },
          { label: 'Errores',         value: errRuns,         color: errRuns > 0 ? 'var(--red)' : 'var(--muted)', show: !!(resellerPerms?.can_manage_jobs || resellerPerms?.can_view_reports) },
          { label: 'Backups totales', value: runs.length,     color: 'var(--accent)', show: !!(resellerPerms?.can_manage_jobs || resellerPerms?.can_view_reports) },
        ].filter(s => s.show).map((s, i) => (
          <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.color }} />
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 10 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Accesos rápidos */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {resellerPerms?.can_view_clients  && <button onClick={() => navigate('/clients')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Ver Clientes →</button>}
        {resellerPerms?.can_manage_jobs   && <button onClick={() => navigate('/jobs')}    style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Ver Jobs →</button>}
        {resellerPerms?.can_view_reports  && <button onClick={() => navigate('/logs')}    style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Ver Logs →</button>}
      </div>

      {/* Clientes con storage */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Mis Clientes</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!resellerPerms?.can_view_clients && (
            <div style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'var(--mono)' }}>Sin acceso a clientes</div>
          )}
          {resellerPerms?.can_view_clients && clients.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'var(--mono)' }}>Sin clientes asignados</div>}
          {resellerPerms?.can_view_clients && clients.map((c, i) => {
            const pct   = c.storage_pct ?? 0;
            const color = pct >= 90 ? 'var(--red)' : pct >= 85 ? 'var(--yellow)' : 'var(--accent)';
            return (
              <div key={i} onClick={() => navigate(`/clients/${c.id}`)} style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{c.plan?.name || 'Sin plan'} · {c.city || '—'}</div>
                </div>
                {c.storage_quota_gb != null && (
                  <div style={{ minWidth: 160 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontFamily: 'var(--mono)', marginBottom: 4 }}>
                      <span style={{ color: 'var(--muted)' }}>{(c.storage_used_gb ?? 0).toFixed(1)} / {c.storage_quota_gb} GB</span>
                      <span style={{ color }}>{pct.toFixed(1)}%</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 2 }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* SERVERS OVERVIEW */}
      <div style={{ marginBottom: 16 }}>
        <ServersOverviewCard userRole="reseller" />
      </div>

      {/* Últimos runs */}
      {resellerPerms?.can_view_reports && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 16 }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>Últimos Backups</span>
            {resellerPerms?.can_manage_jobs && <button onClick={() => navigate('/jobs')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '6px 12px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Ver todos</button>}
          </div>
          <div style={{ padding: 20 }}>
            {runs.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'var(--mono)' }}>Sin backups todavía</div>}
            {runs.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < runs.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor[r.status] || 'var(--muted)', boxShadow: `0 0 6px ${statusColor[r.status] || 'var(--muted)'}`, flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 500 }}>{r.job_name}</span>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>{r.client}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{formatBytes(r.size_bytes)}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{timeAgo(r.started_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Panel de storage del reseller */}
      {me?.storage_quota_gb > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 20, marginBottom: 24 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 12 }}>
            Storage asignado
          </div>
          {(() => {
            const used  = me.storage_used_gb || 0;
            const quota = me.storage_quota_gb;
            const pct   = quota > 0 ? (used / quota) * 100 : 0;
            const color = pct >= 90 ? 'var(--red)' : pct >= 80 ? 'var(--yellow)' : 'var(--accent)';
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 8 }}>
                  <span>{used.toFixed(1)} GB <span style={{ color: 'var(--muted)' }}>/ {quota} GB</span></span>
                  <span style={{ color }}>{pct.toFixed(1)}%</span>
                </div>
                <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 3 }} />
                </div>
                {pct >= 80 && (
                  <div style={{ marginTop: 8, fontSize: 11, color, fontFamily: 'var(--mono)' }}>
                    {pct >= 90 ? '⚠ STORAGE CRÍTICO' : '⚠ Storage alto'}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {activity.length > 0 && <ActivityChart activity={activity} />}
    </div>
  );
}

export default function Dashboard() {
  const { isClient, isReseller, me } = useAuth();
  if (isClient   && me?.client_id) return <ClientDashboard clientId={me.client_id} />;
  if (isReseller) return <ResellerDashboard />;
  return <AdminDashboard />;
}
